import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation, useOutletContext, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { PageHeader, SectionHeader } from '../components/PageHeader';
import type { Channel } from '../channels';
import { sectionFor, WORKSPACE_SECTIONS, workspacePath } from '../nav';

export type Role = 'owner' | 'admin' | 'member';

/** How long this workspace keeps its audit log, and the limits it may choose between. */
export interface AuditRetention {
  months: number;
  maxMonths: number;
  floorMonths: number;
  contact: string;
}

export interface WorkspaceContext {
  tenantId: string;
  name: string;
  role: Role;
  canManage: boolean;
  channels: Channel[];
  auditRetention: AuditRetention;
  reload: () => Promise<void>;
}

export function useWorkspace(): WorkspaceContext {
  return useOutletContext<WorkspaceContext>();
}

/**
 * A workspace: the company using Kruvi Access, its people, and the Salesforce orgs it connects to.
 *
 * Called a workspace rather than an organisation because a customer org is also an organisation, and two
 * things with the same name one click apart is how somebody grants access to the wrong one.
 */
export function Workspace() {
  const { tenantId = '' } = useParams();
  const { pathname } = useLocation();
  const [state, setState] = useState<{ name: string; role: Role; channels: Channel[]; auditRetention: AuditRetention } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const t = await api<{
        tenant: { name: string; slug: string; channels: Channel[]; auditRetention: AuditRetention };
        role: Role;
      }>(`/api/tenants/${tenantId}`);
      setState({
        name: t.tenant.name,
        role: t.role,
        channels: t.tenant.channels,
        auditRetention: t.tenant.auditRetention,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load this workspace');
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!state) return error ? <div className="alert">{error}</div> : <p className="muted">Loading…</p>;
  const canManage = state.role === 'owner' || state.role === 'admin';
  const base = `/workspaces/${tenantId}`;

  const context: WorkspaceContext = {
    tenantId,
    name: state.name,
    role: state.role,
    canManage,
    channels: state.channels,
    auditRetention: state.auditRetention,
    reload: load,
  };

  const section = sectionFor(WORKSPACE_SECTIONS, base, pathname);
  const roleText = state.role === 'owner' ? 'an owner' : state.role === 'admin' ? 'an admin' : 'a member';

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Workspaces', to: '/' }, { label: state.name, to: workspacePath(tenantId) }]}
        title={state.name}
        meta={<>Workspace · you are {roleText} here</>}
      />
      {section && (
        <SectionHeader
          title={section.label}
          blurb={section.path === 'audit' && !canManage ? 'The Salesforce sessions Kruvi Access has opened for you.' : section.blurb}
        />
      )}

      <Outlet context={context} />
    </>
  );
}
